/**
 * @file All permissions checks related to topics
 */

import { TopicWithMembersFragment } from '../../generated/graphql';
import { PermissionsTopic } from '../../models/permissions';
import { MessagePermissionCheckTopicMember } from '../../models/topic';
import { reportError } from '../../services/reporting';

import {
  TOPIC__ADMIN,
  TOPIC__CREATE,
  TOPIC__DELETE,
  TOPIC__UPDATE,
  TOPIC__VIEW,
} from './permissionsAll';
import { getIsBasic, getPermissionsCurrent, getUserCurrent } from './storage';

type TopicCreator = TopicWithMembersFragment['creator'];

/**
 *
 * HELPER
 *
 */

/**
 * Check whether the user has a specific permission
 *
 * @param which Which permission we want to check against
 * @returns     Whether it's true
 */
const hasPermission = (which: PermissionsTopic): boolean => {
  return getPermissionsCurrent().includes(which);
};

/**
 * Check whether the current user has admin permissions for topics
 *
 * @returns Whether it's true
 */
const isAdmin = (): boolean => {
  return getPermissionsCurrent().includes(TOPIC__ADMIN);
};

/**
 * Check whether the current user is author of a specific topic
 *
 * @param topic The topic to check permissions for
 * @returns     Whether it's true
 */
const isAuthor = (topic: TopicCreator): boolean => {
  // Will happen for auto-generated topics
  // Which will happen when concierge form is posted 1st time
  if (topic === null || topic === undefined) {
    return false;
  }

  const user = getUserCurrent();
  return user !== null && user.id === topic.id;
};

/**
 * Check whether the current user is a member of a specified topic
 *
 * @param topic The topic to check
 * @returns     Whether it's true
 */
export const getIsMember = (
  topic: TopicWithMembersFragment | MessagePermissionCheckTopicMember,
): boolean => {
  const user = getUserCurrent();

  if (user === null) {
    reportError('getIsMember: Reading user current before initializing it.');
    return false;
  }

  if (topic.members.findIndex(member => member.id === user.id) !== -1) {
    return true;
  }

  if (topic.isPrivate === false) {
    return true;
  }

  return (
    topic.groups.filter(group =>
      group.members.some(member => member.id === user.id),
    ).length !== 0
  );
};

/**
 *
 * CREATE
 *
 */

/**
 * Get whether the user can access a topic; Either because they're
 * - A member directly
 * - A member of a group that is added to the topic
 * - An admin (they can access all topics)
 *
 * @param topic The topic to check for
 * @returns     Whether the user can access the topic
 */
export const getCanAccess = (topic: TopicWithMembersFragment): boolean => {
  return topic.isPrivate === false || isAdmin() || getIsMember(topic);
};

/**
 * Check whether the user is allowed to add members to a topic
 *
 * @param topic The topic to check for
 * @returns     Whether the user has permissions
 */
export const canAddMember = (topic: TopicWithMembersFragment): boolean => {
  if (topic.isPrivate === false) {
    return false;
  }

  if (isAdmin()) {
    return true;
  }

  if (hasPermission(TOPIC__UPDATE) === false) {
    return false;
  }

  return isAuthor(topic.creator) || getIsMember(topic);
};

/**
 * Check whether the user has permissions
 * to create a topic
 *
 * @returns Whether the user has permissions
 */
export const canCreateTopic = (): boolean => {
  if (getIsBasic() === true) {
    return false;
  }

  return isAdmin() || hasPermission(TOPIC__CREATE);
};

/**
 *
 * READ
 *
 */

/**
 * Check whether the current user can view a topic's members
 *
 * @param topic The topic to check permissions for
 * @returns     Whether the user has permissions
 */
export const canViewMembers = (topic: TopicWithMembersFragment): boolean => {
  if (isAdmin()) {
    return true;
  }

  return (
    hasPermission(TOPIC__VIEW) &&
    (isAuthor(topic.creator) || getIsMember(topic))
  );
};

/**
 *
 * UPDATE
 *
 */

/**
 * Check whether a user is allowed to update topic data
 * (title, description...)
 *
 * @param topic The topic creator to check for
 * @returns     Whether the user has permissions
 */
export const canUpdateTopic = (topic: TopicWithMembersFragment): boolean => {
  if (isAdmin()) {
    return true;
  }

  if (hasPermission(TOPIC__UPDATE) === false) {
    return false;
  }

  return isAuthor(topic.creator) || (topic.isPrivate && getIsMember(topic));
};

/**
 *
 * DELETE
 *
 */

/**
 * Check whether the user is allowed to delete a specific topic
 *
 * @param topic The topic creator to check for
 * @returns     Whether the user has permissions
 */
export const canDeleteTopic = (topic: TopicWithMembersFragment): boolean => {
  return isAdmin() || (isAuthor(topic.creator) && hasPermission(TOPIC__DELETE));
};
